/**
 * airan-ntn — Model Helpers
 * Neural network weight operations, forward pass, loss functions
 */

import { ModelWeights, TrainSample } from './types.js';
import { random, randn } from './channel.js';

// ──────────────────────────────────────────────────────────────────────────────
// Weight Helpers
// ──────────────────────────────────────────────────────────────────────────────

export function zeroWeights(numFeatures: number, numClasses: number): ModelWeights {
  return {
    w: new Float32Array(numClasses * numFeatures).map(() => (random() - 0.5) * 0.01),
    b: new Float32Array(numClasses).fill(0),
    numFeatures,
    numClasses,
    version: 0
  };
}

export function cloneWeights(m: ModelWeights): ModelWeights {
  return { w: new Float32Array(m.w), b: new Float32Array(m.b), numFeatures: m.numFeatures, numClasses: m.numClasses, version: m.version };
}

export function scaleWeights(m: ModelWeights, s: number): ModelWeights {
  return { w: new Float32Array(m.w.map(v => v * s)), b: new Float32Array(m.b.map(v => v * s)), numFeatures: m.numFeatures, numClasses: m.numClasses, version: m.version };
}

export function addWeights(a: ModelWeights, b: ModelWeights): ModelWeights {
  return { w: new Float32Array(a.w.map((v, i) => v + b.w[i])), b: new Float32Array(a.b.map((v, i) => v + b.b[i])), numFeatures: a.numFeatures, numClasses: a.numClasses, version: a.version };
}

export function weightDiffNorm2(a: ModelWeights, b: ModelWeights): number {
  let s = 0;
  for (let i = 0; i < a.w.length; i++) s += (a.w[i] - b.w[i]) ** 2;
  for (let i = 0; i < a.b.length; i++) s += (a.b[i] - b.b[i]) ** 2;
  return s;
}

export function paramCount(m: ModelWeights): number {
  return m.w.length + m.b.length;
}

// ──────────────────────────────────────────────────────────────────────────────
// Softmax & Forward Pass
// ──────────────────────────────────────────────────────────────────────────────

function softmax(logits: number[]): number[] {
  const max = Math.max(...logits);
  const exps = logits.map(v => Math.exp(v - max));
  const sum = exps.reduce((a, b) => a + b, 0);
  return exps.map(v => v / sum);
}

export function forward(x: Float32Array, m: ModelWeights): number[] {
  const { w, b, numFeatures, numClasses } = m;
  const logits = new Array(numClasses).fill(0);
  for (let c = 0; c < numClasses; c++) {
    let z = b[c];
    for (let f = 0; f < numFeatures; f++) z += w[c * numFeatures + f] * x[f];
    logits[c] = z;
  }
  return softmax(logits);
}

// ──────────────────────────────────────────────────────────────────────────────
// Loss & Accuracy
// ──────────────────────────────────────────────────────────────────────────────

export function crossEntropyLoss(samples: TrainSample[], m: ModelWeights): number {
  if (samples.length === 0) return 0;
  let total = 0;
  for (const s of samples) {
    const probs = forward(s.x, m);
    total -= Math.log(Math.max(probs[s.y], 1e-12));
  }
  return total / samples.length;
}

export function accuracy(samples: TrainSample[], m: ModelWeights): number {
  if (samples.length === 0) return 0;
  let correct = 0;
  for (const s of samples) {
    const probs = forward(s.x, m);
    const pred = probs.indexOf(Math.max(...probs));
    if (pred === s.y) correct++;
  }
  return correct / samples.length;
}

// ──────────────────────────────────────────────────────────────────────────────
// SGD Step
// ──────────────────────────────────────────────────────────────────────────────

export function sgdStep(
  m: ModelWeights,
  samples: TrainSample[],
  lr: number,
  proxWeight: number = 0,
  ref: ModelWeights | null = null
): void {
  const { numFeatures, numClasses } = m;
  const N = samples.length;
  if (N === 0) return;

  const dW = new Float32Array(numClasses * numFeatures);
  const dB = new Float32Array(numClasses);

  for (const s of samples) {
    const probs = forward(s.x, m);
    for (let c = 0; c < numClasses; c++) {
      const delta = probs[c] - (c === s.y ? 1 : 0);
      dB[c] += delta / N;
      for (let f = 0; f < numFeatures; f++) {
        dW[c * numFeatures + f] += delta * s.x[f] / N;
      }
    }
  }

  // Proximal gradient (FedProx): add μ*(w - w_ref) to gradient
  if (proxWeight > 0 && ref !== null) {
    for (let i = 0; i < dW.length; i++) dW[i] += proxWeight * (m.w[i] - ref.w[i]);
    for (let i = 0; i < dB.length; i++) dB[i] += proxWeight * (m.b[i] - ref.b[i]);
  }

  for (let i = 0; i < m.w.length; i++) m.w[i] -= lr * dW[i];
  for (let i = 0; i < m.b.length; i++) m.b[i] -= lr * dB[i];
}

// ──────────────────────────────────────────────────────────────────────────────
// Gradient Computation (for FedSGD)
// ──────────────────────────────────────────────────────────────────────────────

export function computeGradient(
  m: ModelWeights,
  samples: TrainSample[]
): { gW: Float32Array; gB: Float32Array } {
  const { numFeatures, numClasses } = m;
  const N = samples.length;
  const gW = new Float32Array(numClasses * numFeatures);
  const gB = new Float32Array(numClasses);
  if (N === 0) return { gW, gB };
  for (const s of samples) {
    const probs = forward(s.x, m);
    for (let c = 0; c < numClasses; c++) {
      const delta = probs[c] - (c === s.y ? 1 : 0);
      gB[c] += delta / N;
      for (let f = 0; f < numFeatures; f++) gW[c * numFeatures + f] += delta * s.x[f] / N;
    }
  }
  return { gW, gB };
}